/**
 * TerraMatrix Universal Data Platform - SourceCard Component
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * Card displaying data source info with status indicator and schema actions.
 */

import React from 'react';
import { DataSource } from '@/services/api/dataApi';

interface SourceCardProps {
  source: DataSource;
  onClick?: () => void;
  onDiscoverSchema?: (source: DataSource) => void;
  onManageSchema?: (source: DataSource) => void;
  isDiscovering?: boolean;
}

const STATUS_COLORS: Record<DataSource['status'], { bg: string; text: string }> = {
  active: { bg: 'bg-green-500', text: 'Active' },
  pending: { bg: 'bg-yellow-500', text: 'Pending' },
  error: { bg: 'bg-red-500', text: 'Error' },
  disabled: { bg: 'bg-gray-500', text: 'Disabled' },
};

const TYPE_ICONS: Record<DataSource['type'], string> = {
  mongodb: '🍃',
  mysql: '🐬',
  postgresql: '🐘',
};

export const SourceCard: React.FC<SourceCardProps> = ({
  source,
  onClick,
  onDiscoverSchema,
  onManageSchema,
  isDiscovering = false,
}) => {
  const statusInfo = STATUS_COLORS[source.status];
  const typeIcon = TYPE_ICONS[source.type] || '💾';
  const hasSchema = source.schema?.tables && source.schema.tables.length > 0;

  const handleSchemaAction = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (hasSchema) {
      onManageSchema?.(source);
    } else {
      onDiscoverSchema?.(source);
    }
  };

  return (
    <div
      onClick={onClick}
      className="border border-border rounded-lg p-3 hover:border-primary cursor-pointer
                 transition-colors bg-card"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg" role="img" aria-label={source.type}>
            {typeIcon}
          </span>
          <div>
            <h3 className="font-medium">{source.name}</h3>
            <p className="text-xs text-muted-foreground capitalize">{source.type}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className={`w-2 h-2 rounded-full ${statusInfo.bg}`}
            title={statusInfo.text}
          />
          <span className="text-xs text-muted-foreground">{statusInfo.text}</span>
        </div>
      </div>

      {/* Description */}
      {source.metadata?.description && (
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
          {source.metadata.description}
        </p>
      )}

      {/* Tags */}
      {source.metadata?.tags && source.metadata.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {source.metadata.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 bg-muted rounded-full"
            >
              {tag}
            </span>
          ))}
          {source.metadata.tags.length > 4 && (
            <span className="text-xs px-2 py-0.5 text-muted-foreground">
              +{source.metadata.tags.length - 4}
            </span>
          )}
        </div>
      )}

      {/* Schema Info & Actions */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
        <div className="text-xs text-muted-foreground">
          {hasSchema ? (
            <>
              {source.schema!.tables.length} table
              {source.schema!.tables.length !== 1 ? 's' : ''}
            </>
          ) : (
            <span className="text-amber-600">No schema discovered</span>
          )}
        </div>
        {(onDiscoverSchema || onManageSchema) && (
          <button
            onClick={handleSchemaAction}
            disabled={isDiscovering}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              isDiscovering
                ? 'bg-muted text-muted-foreground cursor-wait'
                : hasSchema
                ? 'bg-primary/10 text-primary hover:bg-primary/20'
                : 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20'
            }`}
          >
            {isDiscovering ? (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Discovering...
              </span>
            ) : hasSchema ? (
              'Manage Schema'
            ) : (
              'Discover Schema'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SourceCard;
